import api from './api';

/**
 * @typedef {Object} InventoryItem
 * @property {string} id
 * @property {string} ingredientId
 * @property {string} ingredientName
 * @property {string} unit
 * @property {number} currentStock
 * @property {number} minThreshold
 * @property {number} maxCapacity
 * @property {string} updatedAt
 * @property {number} version
 */

const inventoryApi = {
  /**
   * List inventory items
   * @param {Object} params
   * @returns {Promise<{items: InventoryItem[], total: number}>}
   */
  async list(params = {}) {
    console.log('Base url is: ', api.defaults.baseURL);
    const { data } = await api.get('/api/v1/inventory/', { params });
    return data;
  },

  /**
   * Get item by id
   * @param {string} id
   * @returns {Promise<InventoryItem>}
   */
  async get(id) {
    const { data } = await api.get(`/api/v1/inventory/${id}`);
    return data;
  },

  /**
   * Create item
   * @param {Partial<InventoryItem>} payload
   * @returns {Promise<InventoryItem>}
   */
  async create(payload) {
    const { data } = await api.post('/api/v1/inventory', payload);
    return data;
  },

  /**
   * Update item with optimistic concurrency support
   * @param {string} id
   * @param {Partial<InventoryItem>} payload
   * @param {{updatedAt?: string, version?: number}} [options]
   * @returns {Promise<InventoryItem>}
   */
  async update(id, payload, options = {}) {
    const headers = {};
    if (options.updatedAt) headers['If-Unmodified-Since'] = options.updatedAt;
    if (typeof options.version === 'number') headers['If-Match'] = `${options.version}`;
    const { data } = await api.put(`/api/v1/inventory/${id}`, payload, { headers });
    return data;
  },

  /**
   * Delete item
   * @param {string} id
   * @returns {Promise<void>}
   */
  async remove(id) {
    await api.delete(`/api/v1/inventory/${id}`);
  },

  /**
   * Lookup by ingredient id
   * @param {string} ingredientId
   * @returns {Promise<InventoryItem>}
   */
  async byIngredient(ingredientId) {
    const { data } = await api.get(`/api/v1/inventory/by-ingredient/${ingredientId}`);
    return data;
  },

  /**
   * Update quantity set or delta
   * @param {string} id
   * @param {{set?: number, delta?: number}} payload
   * @returns {Promise<InventoryItem>}
   */
  async updateQuantity(id, payload) {
    const { data } = await api.put(`/api/v1/inventory/${id}/quantity`, payload);
    return data;
  },

  /**
   * Bulk update items
   * @param {Array<Partial<InventoryItem> & {id: string}>} items
   * @returns {Promise<{updated: InventoryItem[], errors: Array<{id: string, error: string}>}>}
   */
  async bulkUpdate(items) {
    const { data } = await api.put('/api/v1/inventory/bulk-update', items);
    return data;
  },

  /**
   * Create or update items
   * @param {Array<Partial<InventoryItem> & {ingredientId: string}>} items
   * @returns {Promise<any>}
   */
  async upsert(items) {
    const { data } = await api.post('/api/v1/inventory/upsert', { items });
    return data;
  },

  /**
   * Production capacity calculation based on current inventory
   * @param {Object} params
   * @returns {Promise<any>}
   */
  async productionCapacity(params = {}) {
    const { data } = await api.post('/api/v1/inventory/production-capacity', {});
    return data;
  },

  /**
   * Alerts summary for inventory
   * @param {Object} params
   * @returns {Promise<any>}
   */
  async alerts(params = {}) {
    const { data } = await api.get('/api/v1/inventory/alerts/summary', { params });
    return data;
  },

  /**
   * Inventory audit trail
   * @param {Object} params
   * @returns {Promise<{items: Array<{id: string, entityId: string, field: string, oldValue: any, newValue: any, actor: string, timestamp: string}>, total: number}>}
   */
  async auditTrail(params = {}) {
    const { data } = await api.get('/api/v1/inventory/audit', { params });
    return data;
  }
};

export default inventoryApi;